(() => {
  'use strict';

  const protectedGroups = ['august-pre-order', 'september-pre-order', 'october-pre-order'];
  const initialGroups = window.preorderCartInitialGroups || {};
  const modal = document.getElementById('PreorderCartGuard');
  const message = document.getElementById('PreorderCartGuardMessage');
  const confirm = modal && modal.querySelector('.preorder-cart-guard__confirm');
  const bypassCheckout = new WeakSet();
  const bypassCheckoutForm = new WeakSet();
  const productCache = new Map();
  const nativeFetch = window.fetch.bind(window);
  let returnFocus = null;
  let addQueue = Promise.resolve();

  const root = () => window.Shopify?.routes?.root || '/';
  const url = (path) => root() + path;
  const normalise = (value) => String(value || '').trim().toLowerCase();
  const groupFromTags = (tags) => {
    const list = (Array.isArray(tags) ? tags : String(tags || '').split(','))
      .map(normalise);
    return protectedGroups.find((group) => list.includes(group)) || 'standard';
  };

  function showModal(text, focusTarget) {
    if (!modal) return;
    message.textContent = text;
    returnFocus = focusTarget instanceof HTMLElement ? focusTarget : null;
    modal.hidden = false;
    document.body.classList.add('preorder-cart-guard-open');
    window.requestAnimationFrame(() => confirm?.focus());
  }

  function showConflict(incomingGroup, focusTarget) {
    const incomingIsPreorder = incomingGroup !== 'standard';
    const text = incomingIsPreorder
      ? 'Your basket already contains items for a different delivery date.\n\nOctober and other pre-orders must be purchased separately from regular products and from other pre-order months.'
      : 'Your basket already contains pre-order items.\n\nRegular, ready-to-ship products must be purchased separately from pre-orders.';
    showModal(text, focusTarget);
  }

  function showCheckError(focusTarget) {
    showModal('We could not check your basket just now. Please try again.', focusTarget);
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('preorder-cart-guard-open');
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    returnFocus = null;
  }

  modal?.querySelectorAll('[data-preorder-cart-guard-close]').forEach((button) => {
    button.addEventListener('click', closeModal);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  async function fetchCart() {
    const response = await nativeFetch(url('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  async function groupForItem(item) {
    const marker = normalise(item.properties?._preorder_group);
    if (protectedGroups.includes(marker)) return marker;
    if (normalise(item.properties?._september_pre_order) === 'true') {
      return 'september-pre-order';
    }
    if (Object.prototype.hasOwnProperty.call(initialGroups, item.key)) {
      return initialGroups[item.key];
    }
    if (!item.handle) throw new Error('Cart item handle missing');
    if (!productCache.has(item.handle)) {
      const request = nativeFetch(url(`products/${encodeURIComponent(item.handle)}.js`), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Unable to load product ${item.handle}`);
        return response.json();
      });
      productCache.set(item.handle, request);
    }
    try {
      const product = await productCache.get(item.handle);
      return groupFromTags(product.tags);
    } catch (error) {
      productCache.delete(item.handle);
      throw error;
    }
  }

  async function inspectCart() {
    const cart = await fetchCart();
    const groups = await Promise.all((cart.items || []).map(groupForItem));
    return { cart, groups: [...new Set(groups)] };
  }

  const conflictsWith = (groups, incomingGroup) =>
    groups.some((group) => group !== incomingGroup);

  function isAjaxAdd(input, init) {
    const rawUrl = typeof input === 'string' ? input : input?.url || String(input || '');
    const method = init?.method || input?.method || 'GET';
    return /\/cart\/add\.js(?:\?|$)/.test(rawUrl || '') &&
      normalise(method) === 'post';
  }

  function incomingIds(body) {
    let payload = body;
    if (typeof payload === 'string') {
      try { payload = JSON.parse(payload); }
      catch { payload = new URLSearchParams(payload); }
    }
    if (payload instanceof FormData || payload instanceof URLSearchParams) {
      return [String(payload.get('id') || '')];
    }
    if (payload && Array.isArray(payload.items)) {
      return payload.items.map((item) => String(item.id || ''));
    }
    return payload?.id ? [String(payload.id)] : [];
  }

  function groupForVariant(id) {
    for (const form of document.querySelectorAll('form[data-preorder-cart-group]')) {
      if (form.querySelector('input[name="id"]')?.value === id) {
        return form.dataset.preorderCartGroup || 'standard';
      }
    }
    // Unmarked AJAX adds (birthday gifts, upsells, etc.) are regular products.
    return 'standard';
  }

  function rejectedResponse() {
    return new Response(JSON.stringify({ description: 'Pre-order items must be ordered separately.' }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  window.fetch = (input, init) => {
    if (!isAjaxAdd(input, init)) return nativeFetch(input, init);
    const attempt = async () => {
      const body = init?.body === undefined && input instanceof Request
        ? await input.clone().text()
        : init?.body;
      const ids = incomingIds(body);
      const incomingGroups = [...new Set(ids.map(groupForVariant))];
      if (incomingGroups.length === 0) {
        showCheckError();
        return rejectedResponse();
      }
      try {
        const current = await inspectCart();
        if (incomingGroups.length > 1 || conflictsWith(current.groups, incomingGroups[0])) {
          showConflict(incomingGroups[0]);
          return rejectedResponse();
        }
      } catch (error) {
        console.error('[PreorderCartGuard] AJAX add check failed', error);
        showCheckError();
        return rejectedResponse();
      }
      return nativeFetch(input, init);
    };
    const result = addQueue.then(attempt, attempt);
    addQueue = result.then(() => undefined, () => undefined);
    return result;
  };

  document.addEventListener('click', async (event) => {
    const target = event.target.closest?.('.js-checkout, #checkout, [name="checkout"], a[href]');
    if (!target) return;
    const linkUrl = target.matches('a[href]')
      ? new URL(target.href, window.location.href)
      : null;
    const checkoutLink = linkUrl?.origin === window.location.origin &&
      /\/checkout\/?$/.test(linkUrl.pathname);
    if (!checkoutLink && !target.matches('.js-checkout, #checkout, [name="checkout"]')) return;
    if (bypassCheckout.has(target)) {
      bypassCheckout.delete(target);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (target.dataset.preorderGuardChecking === 'true') return;
    target.dataset.preorderGuardChecking = 'true';
    try {
      const current = await inspectCart();
      if (current.groups.length > 1) {
        showConflict('standard', target);
        return;
      }
      if (checkoutLink) {
        window.location.assign(linkUrl.href);
      } else {
        if (target.form) bypassCheckoutForm.add(target.form);
        bypassCheckout.add(target);
        target.click();
      }
    } catch (error) {
      bypassCheckout.delete(target);
      if (target.form) bypassCheckoutForm.delete(target.form);
      console.error('[PreorderCartGuard] Checkout check failed', error);
      showCheckError(target);
    } finally {
      delete target.dataset.preorderGuardChecking;
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout') return;
    if (bypassCheckoutForm.has(form)) {
      bypassCheckoutForm.delete(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.preorderCheckoutPending === 'true') return;
    form.dataset.preorderCheckoutPending = 'true';
    try {
      const current = await inspectCart();
      if (current.groups.length > 1) {
        showConflict('standard', submitter);
        return;
      }
      bypassCheckoutForm.add(form);
      form.requestSubmit(submitter);
    } catch (error) {
      bypassCheckoutForm.delete(form);
      console.error('[PreorderCartGuard] Checkout form check failed', error);
      showCheckError(submitter);
    } finally {
      delete form.dataset.preorderCheckoutPending;
    }
  }, true);

  window.PreorderCartGuard = { inspectCart };
})();
